import { SURF_SPEAKER_SCRIPT_MODEL_ID, SurfCloudAPI } from "../graphql/surfApi.js";
import { allocateBudget } from "./budgetAllocator.js";
import { estimateSeconds } from "./scriptEstimate.js";
import { slideTextHash } from "./scriptHash.js";
import { resolveSpeakerScriptConfig, setSpeakerScript, setSpeakerScriptConfig, } from "./scriptOps.js";
import { buildSpeakerScriptUserPrompt, parseSpeakerScriptResponse, resolveSpeakerScriptSystemPrompt, } from "./speakerScriptPrompt.js";
import { pollSurfAIJob } from "../services/surfAiJob.js";
import { encodeSurfBedrockTargetParams } from "../services/surfJobTarget.js";
export const SURF_SPEAKER_SCRIPT_FEATURE = "surf.speaker.script";
function outlineSectionForSlide(deck, slideId) {
    const slide = deck.slides.find((s) => s.id === slideId);
    if (!slide?.outlineSectionId)
        return undefined;
    return deck.outline?.find((s) => s.id === slide.outlineSectionId);
}
export function resolveTargetSeconds(deck, slideId, override) {
    if (typeof override === "number" && override > 0)
        return override;
    const slide = deck.slides.find((s) => s.id === slideId);
    const fixed = slide?.speakerScript?.targetSeconds;
    if (typeof fixed === "number" && fixed > 0)
        return fixed;
    const budget = allocateBudget(deck);
    return budget.get(slideId) ?? 60;
}
export async function submitSpeakerScriptJob(cfg, deck, slideId, opts) {
    const slideIndex = deck.slides.findIndex((s) => s.id === slideId);
    if (slideIndex < 0)
        throw new Error("Slide non trovata.");
    const config = resolveSpeakerScriptConfig(deck);
    const slide = deck.slides[slideIndex];
    const mode = opts?.mode ?? "generate";
    const targetSeconds = resolveTargetSeconds(deck, slideId, opts?.targetSeconds);
    const tone = opts?.tone ?? slide.speakerScript?.tone ?? config.defaultTone;
    const language = opts?.language ?? slide.speakerScript?.language ?? config.language ?? "it";
    const existingScript = opts?.existingScript !== undefined
        ? opts.existingScript.trim() || undefined
        : mode === "generate"
            ? undefined
            : slide.speakerScript?.text?.trim() || undefined;
    const userText = buildSpeakerScriptUserPrompt({
        deck,
        slide,
        slideIndex,
        outlineSection: outlineSectionForSlide(deck, slideId),
        prevSlide: slideIndex > 0 ? deck.slides[slideIndex - 1] : undefined,
        nextSlide: slideIndex < deck.slides.length - 1 ? deck.slides[slideIndex + 1] : undefined,
        targetSeconds,
        wpm: config.wordsPerMinute,
        tone,
        language,
        mode,
        existingScript,
        references: mode === "rewrite" ?
            slide.speakerScript?.references?.map((r) => ({
                title: r.title,
                snippet: r.snippet,
                url: r.url,
            }))
            : undefined,
    });
    const systemText = resolveSpeakerScriptSystemPrompt(mode, existingScript);
    const applyMeta = { mode, targetSeconds, tone, language, slideIndex };
    const surfTarget = {
        op: "speaker.script",
        deckId: deck.id,
        slideId,
        applyMeta,
    };
    await SurfCloudAPI.ensureProfile(cfg);
    const result = await SurfCloudAPI.submitAIJob(cfg, {
        kind: "BEDROCK_CONVERSE",
        provider: "BEDROCK",
        prompt: "Genera discorso speaker",
        modelId: SURF_SPEAKER_SCRIPT_MODEL_ID,
        feature: SURF_SPEAKER_SCRIPT_FEATURE,
        paramsJson: encodeSurfBedrockTargetParams({ systemText, userText, maxTokens: 4096 }, surfTarget),
        inputAssetKeys: [],
    });
    return {
        jobId: result.jobId,
        deckId: deck.id,
        op: "speaker.script",
        target: { slideId },
        label: `Discorso slide ${slideIndex + 1}`,
        applyMeta,
    };
}
export function applySpeakerScriptText(deck, slideId, text, applyMeta) {
    const wpm = resolveSpeakerScriptConfig(deck).wordsPerMinute;
    const slide = deck.slides.find((s) => s.id === slideId);
    if (!slide)
        return deck;
    const hash = slideTextHash(slide, deck);
    const tone = applyMeta?.tone;
    const language = typeof applyMeta?.language === "string" ? applyMeta.language : undefined;
    const targetSeconds = typeof applyMeta?.targetSeconds === "number" ? applyMeta.targetSeconds : undefined;
    return setSpeakerScript(deck, slideId, {
        text: parseSpeakerScriptResponse(text),
        status: "draft",
        generatedFromHash: hash,
        estimatedSeconds: estimateSeconds(text, wpm),
        tone,
        language,
        targetSeconds,
    });
}
export async function generateSpeakerScript(cfg, deck, slideId, opts) {
    const submit = await submitSpeakerScriptJob(cfg, deck, slideId, opts);
    const raw = await pollSurfAIJob(cfg, submit.jobId);
    const text = parseSpeakerScriptResponse(raw);
    return applySpeakerScriptText(deck, slideId, text, submit.applyMeta);
}
export async function generateAllSpeakerScripts(cfg, deck, onProgress) {
    let next = deck;
    const total = deck.slides.length;
    for (let i = 0; i < total; i += 1) {
        const slide = next.slides[i];
        onProgress?.(i, total, slide.id);
        next = await generateSpeakerScript(cfg, next, slide.id, { mode: "generate" });
    }
    return next;
}
export { setSpeakerScriptConfig };
